import { Link } from 'react-router-dom'
import type { LegalSection } from '@/lib/legal'
import { LEGAL_LAST_UPDATED } from '@/lib/legal'

type LegalDocumentProps = {
  title: string
  intro: string
  sections: LegalSection[]
  alternateLink?: { href: string; label: string }
}

export function LegalDocument({ title, intro, sections, alternateLink }: LegalDocumentProps) {
  return (
    <article className="mx-auto max-w-3xl px-4 py-10 sm:px-6 sm:py-14">
      <p className="text-sm text-muted-foreground">Last updated: {LEGAL_LAST_UPDATED}</p>
      <h1 className="mt-2 text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
        {title}
      </h1>
      <p className="mt-4 text-sm leading-relaxed text-foreground/90 sm:text-base">{intro}</p>

      {alternateLink ? (
        <p className="mt-4 text-sm text-muted-foreground">
          See also{' '}
          <Link to={alternateLink.href} className="font-medium text-primary hover:underline">
            {alternateLink.label}
          </Link>
          .
        </p>
      ) : null}

      <div className="mt-10 space-y-8">
        {sections.map((section) => (
          <section key={section.id} id={section.id} className="scroll-mt-24">
            <h2 className="text-lg font-semibold tracking-tight text-foreground">{section.title}</h2>
            <div className="mt-3 space-y-3 text-sm leading-relaxed text-foreground/90">
              {section.paragraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
              {section.bullets ? (
                <ul className="list-disc space-y-2 pl-5">
                  {section.bullets.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              ) : null}
              {section.paragraphsAfterBullets?.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
          </section>
        ))}
      </div>

      <p className="mt-10 rounded-lg border border-amber-200/80 bg-amber-50/80 px-4 py-3 text-sm leading-relaxed text-amber-950">
        <strong className="font-medium">Early access notice.</strong> Lexio is in active testing.
        These documents are a working draft and may be revised before general availability. They are
        not a substitute for professional legal advice.
      </p>
    </article>
  )
}
